<template>
  <BasicTemplate>
    <v-card class="game-ordering">
      <v-card-title class="d-flex align-center">
        <h3 class="d-flex align-center mb-0">
          <v-icon class="mr-2">mdi-sort</v-icon>
          Global Game Ordering
        </h3>
      </v-card-title>

      <v-dialog v-model="saving" width="480" persistent>
        <v-card class="admin-dialog">
          <v-card-title class="admin-dialog__title">
            Saving game order...
          </v-card-title>
          <v-card-text class="admin-dialog__body">
            <v-progress-linear
              :value="savingProgress"
              :indeterminate="!savingTotal"
              color="primary"
              background-color="grey lighten-3"
              height="8"
              rounded
              class="mb-3"
            />
            <div class="text-center">
              {{ savingProgressText }}
            </div>
          </v-card-text>
        </v-card>
      </v-dialog>

      <v-card-text>
        <v-alert v-if="error" type="error" class="mb-4">
          {{ error }}
        </v-alert>

        <v-card class="mb-4 game-ordering__games" elevation="1">
          <v-card-title class="d-flex align-center">
            <v-icon class="mr-2">mdi-gamepad-variant</v-icon>
            <span class="font-weight-bold">Games</span>
            <v-spacer />
            <v-chip small class="mr-2" color="info">
              {{ games.length }} games
            </v-chip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  class="mr-2"
                  color="secondary"
                  v-bind="attrs"
                  v-on="on"
                  :disabled="!games.length"
                  data-testid="game-ordering-auto-order"
                  @click="autoOrderGames"
                >
                  mdi-auto-fix
                </v-icon>
              </template>
              <span>Auto-order all games sequentially</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  color="secondary"
                  v-bind="attrs"
                  v-on="on"
                  :disabled="!games.length"
                  data-testid="game-ordering-clear-orders"
                  @click="clearAllOrders"
                >
                  mdi-broom
                </v-icon>
              </template>
              <span>Clear all order values</span>
            </v-tooltip>
          </v-card-title>

          <v-card-text class="game-ordering__search py-1">
            <v-text-field
              v-model="search"
              label="Search"
              dense
              hide-details
              clearable
              class="mt-0"
              prepend-icon="mdi-magnify"
            />
          </v-card-text>

          <v-data-table
            class="game-ordering__table"
            :headers="headers"
            :items="filteredGames"
            :loading="loading"
            item-key="id"
            :items-per-page="50"
            :footer-props="{ itemsPerPageOptions: [25, 50, 100, 200] }"
          >
            <template v-slot:item.name="{ item: game }">
              <div class="game-name">{{ game.name }}</div>
              <div class="game-slug grey--text">{{ game.slug }}</div>
            </template>
            <template v-slot:item.order="{ item: game }">
              <v-text-field
                v-model="game.order"
                type="number"
                min="1"
                :max="games.length"
                dense
                hide-details
                class="order-input"
                @change="updateOrder(game)"
                @input="handleOrderInput(game, $event)"
              />
            </template>
            <template v-slot:item.actions="{ item: game }">
              <div class="game-ordering__row-actions">
                <v-btn
                  icon
                  small
                  class="order-btn"
                  :disabled="!game.order || game.order <= 1"
                  @click="decrementOrder(game)"
                >
                  <v-icon size="20" color="primary">mdi-arrow-up-bold</v-icon>
                </v-btn>
                <v-btn
                  icon
                  small
                  class="order-btn"
                  :disabled="!game.order || game.order >= games.length"
                  @click="incrementOrder(game)"
                >
                  <v-icon size="20" color="primary">mdi-arrow-down-bold</v-icon>
                </v-btn>
                <v-btn
                  icon
                  small
                  class="order-btn"
                  color="error"
                  :disabled="!game.order"
                  @click="clearOrder(game)"
                >
                  <v-icon size="20">mdi-close</v-icon>
                </v-btn>
              </div>
            </template>
            <template v-slot:no-data>
              <div class="text-center pa-6">
                <v-icon size="64" color="grey lighten-1">mdi-sort-variant</v-icon>
                <p class="mt-2 mb-0 grey--text">No games found.</p>
              </div>
            </template>
          </v-data-table>
        </v-card>

        <div class="game-ordering__save mb-2">
          <v-btn
            color="primary"
            large
            class="mr-2"
            data-testid="game-ordering-save"
            :loading="saving"
            :disabled="!hasChanges"
            @click="saveOrdering"
          >
            Save
          </v-btn>
          <v-btn
            outlined
            large
            data-testid="game-ordering-cancel"
            :disabled="saving || !hasChanges"
            @click="cancelChanges"
          >
            Cancel
          </v-btn>
        </div>
      </v-card-text>
    </v-card>
  </BasicTemplate>
</template>

<script>
import BasicTemplate from '../../templates/basic.vue';

export default {
  name: 'GameOrdering',
  components: {
    BasicTemplate
  },
  data() {
    return {
      loading: false,
      saving: false,
      savingTotal: 0,
      savingCompleted: 0,
      savingStatusText: '',
      error: null,
      games: [],
      search: '',
      initialOrders: {},
      headers: [
        { text: 'Game', value: 'name', sortable: false },
        { text: 'Order', value: 'order', sortable: false, width: '120px' },
        {
          text: 'Actions',
          value: 'actions',
          sortable: false,
          align: 'center',
          width: '1%',
          class: 'game-ordering__col--compact',
          cellClass: 'game-ordering__col--compact',
        },
      ],
    };
  },
  computed: {
    filteredGames() {
      if (!this.search) return this.games;

      const searchLower = this.search.toLowerCase();
      return this.games.filter(game =>
        (game.name || '').toLowerCase().includes(searchLower) ||
        (game.slug || '').toLowerCase().includes(searchLower)
      );
    },
    hasChanges() {
      return this.games.some(game =>
        this.initialOrders[game.id] !== game.order
      );
    },
    savingProgress() {
      if (!this.savingTotal) {
        return 0;
      }
      return Math.round((this.savingCompleted / this.savingTotal) * 100);
    },
    savingProgressText() {
      if (!this.savingTotal) {
        return this.savingStatusText || 'Preparing...';
      }
      return this.savingStatusText
        || `Saved ${this.savingCompleted} / ${this.savingTotal} (${this.savingProgress}%)`;
    },
  },
  async created() {
    await this.loadGames();
  },
  methods: {
    async loadGames() {
      try {
        this.loading = true;
        this.error = null;

        const response = await this.$gameApi.get('/games');
        const raw = Array.isArray(response) ? response : (response.data || []);

        // Keep only fields needed for ordering — full Game entities freeze the UI.
        this.games = raw
          .map((game) => ({
            id: game.id,
            name: game.name,
            slug: game.slug,
            order: game.order == null ? null : Number(game.order),
          }))
          .sort((a, b) => {
            const orderA = a.order == null ? Number.MAX_SAFE_INTEGER : a.order;
            const orderB = b.order == null ? Number.MAX_SAFE_INTEGER : b.order;
            return orderA - orderB;
          });

        this.initialOrders = {};
        this.games.forEach((game) => {
          this.initialOrders[game.id] = game.order;
        });
      } catch (err) {
        console.error('Error loading games:', err);
        this.error = 'Failed to load games. Please try again.';
      } finally {
        this.loading = false;
      }
    },
    cancelChanges() {
      if (!this.hasChanges || this.saving) return;
      this.games = this.games
        .map((game) => ({
          ...game,
          order: this.initialOrders[game.id],
        }))
        .sort((a, b) => {
          const orderA = a.order == null ? Number.MAX_SAFE_INTEGER : a.order;
          const orderB = b.order == null ? Number.MAX_SAFE_INTEGER : b.order;
          return orderA - orderB;
        });
    },
    handleOrderInput(game, value) {
      game.order = value === '' ? null : Number(value);
    },
    updateOrder(game) {
      if (game.order === '' || game.order === null) {
        game.order = null;
        return;
      }
      let newOrder = Number.parseInt(game.order, 10);
      if (isNaN(newOrder) || newOrder < 1) newOrder = 1;
      if (newOrder > this.games.length) newOrder = this.games.length;
      game.order = newOrder;

      const oldIndex = this.games.findIndex(g => g.id === game.id);
      this.games.splice(oldIndex, 1);
      this.games.splice(newOrder - 1, 0, game);

      this.games.forEach((g, i) => {
        g.order = i + 1;
      });
    },
    async saveOrdering() {
      const gamesToUpdate = this.games.filter(game =>
        this.initialOrders[game.id] !== game.order
      );

      if (gamesToUpdate.length === 0) {
        this.$swal.fire({
          icon: 'info',
          title: 'No Changes',
          text: 'No changes to save'
        });
        return;
      }

      const result = await this.$swal.fire({
        title: 'Save Global Ordering?',
        text: `This will update the global order for ${gamesToUpdate.length} game(s).`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes, save it!',
        cancelButtonText: 'No, cancel',
      });
      if (!result.isConfirmed) {
        return;
      }

      try {
        this.saving = true;
        this.error = null;
        this.savingTotal = gamesToUpdate.length;
        this.savingCompleted = 0;
        this.savingStatusText = 'Preparing batches...';
        await this.$nextTick();

        const BATCH_CHUNK_SIZE = 50;
        const operations = gamesToUpdate.map(game => ({
          operation: 'update',
          data: {
            id: game.id,
            order: game.order === null ? null : game.order
          }
        }));
        const totalBatches = Math.ceil(operations.length / BATCH_CHUNK_SIZE);

        for (let i = 0; i < operations.length; i += BATCH_CHUNK_SIZE) {
          const chunk = operations.slice(i, i + BATCH_CHUNK_SIZE);
          const batchIndex = Math.floor(i / BATCH_CHUNK_SIZE) + 1;
          this.savingStatusText = `Saving batch ${batchIndex} / ${totalBatches} (${this.savingCompleted} / ${this.savingTotal})`;
          await this.$nextTick();

          const batchResponse = await this.$gameApi.post('/games/batch', { operations: chunk });
          if (!batchResponse || !batchResponse.success) {
            const firstError = batchResponse?.results?.find(r => r.error);
            throw new Error(firstError?.error || 'Batch save failed');
          }
          this.savingCompleted = Math.min(i + chunk.length, gamesToUpdate.length);
          this.savingStatusText = `Saved ${this.savingCompleted} / ${this.savingTotal} (${this.savingProgress}%)`;
          await this.$nextTick();
        }

        this.savingStatusText = 'Reloading games...';
        gamesToUpdate.forEach(game => {
          this.initialOrders[game.id] = game.order;
        });

        await this.loadGames();

        this.$swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Game order saved successfully',
          timer: 2000,
          showConfirmButton: false,
        });
      } catch (err) {
        console.error('Error saving game order:', err);
        this.error = err.message || 'Failed to save game order. Please try again.';
        this.$swal.fire({
          icon: 'error',
          title: 'Error',
          text: err.message || 'Failed to save game order. Please try again.'
        });
      } finally {
        this.saving = false;
        this.savingTotal = 0;
        this.savingCompleted = 0;
        this.savingStatusText = '';
      }
    },
    incrementOrder(game) {
      const idx = this.games.findIndex(g => g.id === game.id);
      if (idx < this.games.length - 1) {
        const temp = this.games[idx + 1];
        this.$set(this.games, idx + 1, game);
        this.$set(this.games, idx, temp);
        this.games.forEach((g, i) => {
          g.order = i + 1;
        });
      }
    },
    decrementOrder(game) {
      const idx = this.games.findIndex(g => g.id === game.id);
      if (idx > 0) {
        const temp = this.games[idx - 1];
        this.$set(this.games, idx - 1, game);
        this.$set(this.games, idx, temp);
        this.games.forEach((g, i) => {
          g.order = i + 1;
        });
      }
    },
    clearOrder(game) {
      game.order = null;
      this.updateOrder(game);
      this.$nextTick(() => {
        game.order = null;
      });
    },
    autoOrderGames() {
      if (!this.games.length) return;
      this.games.forEach((game, index) => {
        game.order = index + 1;
      });
      this.games = [...this.games];
    },
    clearAllOrders() {
      if (!this.games.length) return;
      this.games = this.games.map((game) => ({
        ...game,
        order: null,
      }));
    },
  }
};
</script>

<style scoped>
.game-ordering__games {
  overflow: hidden;
}

.game-ordering__search {
  max-width: 420px;
}

.game-ordering__table ::v-deep .v-data-table__wrapper {
  overflow-x: auto;
}

.game-ordering__table ::v-deep .game-ordering__col--compact {
  white-space: nowrap;
  width: 1%;
  padding-left: 8px;
  padding-right: 8px;
}

.game-ordering__row-actions {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}

.game-ordering__save {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  position: sticky;
  bottom: 12px;
  z-index: 2;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(25, 118, 210, 0.2);
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  backdrop-filter: blur(6px);
}

.game-name {
  font-weight: 500;
  font-size: 0.875rem;
  line-height: 1.2;
}

.game-slug {
  font-size: 0.75rem;
  line-height: 1.2;
}

.order-input {
  width: 72px;
  max-width: 72px;
}

.order-input ::v-deep input[type='number'] {
  -moz-appearance: textfield;
}

.order-input ::v-deep input[type='number']::-webkit-outer-spin-button,
.order-input ::v-deep input[type='number']::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.order-btn {
  min-width: 24px !important;
  height: 24px !important;
  padding: 0 !important;
  margin: 0 !important;
}

.order-btn:not(.v-btn--disabled) {
  background-color: rgba(0, 0, 0, 0.05);
}

.order-btn:not(.v-btn--disabled):hover {
  background-color: rgba(0, 0, 0, 0.1);
}
</style>
